import { useMemo } from 'react'

// ─────────────────────────────────────────────────────────────
//  LOADING STARS
//  Embroidery-themed loading overlay — animated 4-point sparkle stars
//  drift diagonally across the surface with fading trails. Pure CSS
//  keyframes (no rAF loop), so zero JS overhead while it's visible.
//
//  Usage: render conditionally over any container that has
//  `position: relative`. Component is `pointer-events-none` so it
//  doesn't block clicks on whatever's below.
// ─────────────────────────────────────────────────────────────

const COLORS = ['#fbbf24', '#f59e0b', '#fcd34d', '#fde68a', '#facc15']

export default function LoadingStars ({ density = 16, label }) {
  // Generate stable per-star randomized parameters once on mount.
  const stars = useMemo(() => {
    return Array.from({ length: density }, (_, i) => {
      const seed = i * 31 + 7
      const rand = (n) => ((seed * (n + 1) * 9301 + 49297) % 233280) / 233280
      return {
        startX: rand(1) * 100,           // 0..100% of width
        startY: rand(2) * 30 - 5,        // start near top
        endX:   rand(1) * 100 - rand(3) * 30 + 15,
        endY:   80 + rand(4) * 30,       // travel down to ~bottom
        size:   8 + rand(5) * 16,        // px
        delay:  rand(6) * 5,             // s
        dur:    2.4 + rand(7) * 1.8,     // s
        color:  COLORS[i % COLORS.length],
        rot:    rand(8) * 360,
      }
    })
  }, [density])

  return (
    <>
      <style>{`
        @keyframes zenna-shootstar-pos {
          0%   { left: var(--sx); top: var(--sy); }
          100% { left: var(--ex); top: var(--ey); }
        }
        @keyframes zenna-shootstar-fade {
          0%   { opacity: 0; transform: scale(0.4) rotate(var(--r0)); }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { opacity: 0; transform: scale(1.0) rotate(calc(var(--r0) + 180deg)); }
        }
        @keyframes zenna-pulse-label {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
      `}</style>
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-30">
        {stars.map((s, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              // Animate `left`/`top` directly so % is container-relative.
              // The previous version used transform: translate(50%, 50%)
              // which translates by 50% of the ELEMENT'S own size (16px =
              // 8px) and clustered the stars in the corner.
              '--sx': `${s.startX}%`,
              '--sy': `${s.startY}%`,
              '--ex': `${s.endX}%`,
              '--ey': `${s.endY}%`,
              '--r0': `${s.rot}deg`,
              left: `${s.startX}%`,
              top:  `${s.startY}%`,
              animation: `zenna-shootstar-pos ${s.dur}s ease-in-out ${s.delay}s infinite, zenna-shootstar-fade ${s.dur}s ease-in-out ${s.delay}s infinite`,
              willChange: 'left, top, transform, opacity',
            }}
          >
            {/* 4-point sparkle star — the kind you'd embroider as an
                accent stitch. Two crossed diamonds with a center glow. */}
            <svg
              width={s.size}
              height={s.size}
              viewBox="0 0 32 32"
              style={{
                filter: `drop-shadow(0 0 ${s.size * 0.3}px ${s.color}88)`,
              }}
            >
              <path
                d="M16 2 L18 14 L30 16 L18 18 L16 30 L14 18 L2 16 L14 14 Z"
                fill={s.color}
              />
              <circle cx="16" cy="16" r="2" fill="#ffffff" opacity="0.85" />
            </svg>
          </div>
        ))}
        {label && (
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-amber-200 text-sm font-semibold tracking-wide drop-shadow-lg"
            style={{ animation: 'zenna-pulse-label 1.6s ease-in-out infinite' }}
          >
            {label}
          </div>
        )}
      </div>
    </>
  )
}
